import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InspectionDefect } from '@/types/InspectionDefect';
import { getDefectSeverity } from '@/utils/inspection.utils';
import { DefectSeverity as DefectSeverityEnum } from '@/enums/DefectSeverity.enum';
import { DefectImage } from '@/features/inspection-details/components/defect-list/DefectImage';
import styles from './DefectDetailsTable.module.scss';

export interface DefectDetailsTableProps {
    defects: InspectionDefect[];
    inspectionId: string;
    /** Optional: show compact column headers (e.g. "Cri" instead of "Critical") when false */
    fullView?: boolean;
    responsive?: boolean;
}

export const DefectDetailsTable: React.FC<DefectDetailsTableProps> = ({
    defects,
    inspectionId,
    fullView,
    responsive = true,
}) => {
    const { t: translate } = useTranslation();
    const [autoFullView, setAutoFullView] = useState<boolean>(() => {
        if (typeof fullView === 'boolean') return fullView;
        if (!responsive) return true;
        if (typeof window === 'undefined') return true;
        return window.innerWidth > 600;
    });

    useEffect(() => {
        if (!responsive) return;
        if (typeof fullView === 'boolean') return;
        if (typeof window === 'undefined') return;

        const handleResize = () => setAutoFullView(window.innerWidth > 600);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [fullView, responsive]);

    const isFullView = typeof fullView === 'boolean' ? fullView : autoFullView;

    const { criticalCount, majorCount, minorCount } = useMemo(() => {
        const critical = defects
            .filter((d) => d.defectSeverity === DefectSeverityEnum.CRITICAL)
            .reduce((sum, d) => sum + (d.defectQuantity ?? 0), 0);
        const major = defects
            .filter((d) => d.defectSeverity === DefectSeverityEnum.MAJOR)
            .reduce((sum, d) => sum + (d.defectQuantity ?? 0), 0);
        const minor = defects
            .filter((d) => d.defectSeverity === DefectSeverityEnum.MINOR)
            .reduce((sum, d) => sum + (d.defectQuantity ?? 0), 0);
        return {
            criticalCount: critical,
            majorCount: major,
            minorCount: minor,
        };
    }, [defects]);

    if (!inspectionId) return null;

    return (
        <div className={styles.tableContainer}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>{translate('Code')}</th>
                        <th className={styles.description}>{translate('Description')}</th>
                        <th>
                            {translate(isFullView ? DefectSeverityEnum.CRITICAL : 'Cri')}
                            {criticalCount > 0 && <span className={styles.count}> ({criticalCount})</span>}
                        </th>
                        <th>
                            {translate(isFullView ? DefectSeverityEnum.MAJOR : 'Maj')}
                            {majorCount > 0 && <span className={styles.count}> ({majorCount})</span>}
                        </th>
                        <th>
                            {translate(isFullView ? DefectSeverityEnum.MINOR : 'Min')}
                            {minorCount > 0 && <span className={styles.count}> ({minorCount})</span>}
                        </th>
                        <th className={styles.defectColumn}>{translate(isFullView ? 'Defect' : 'Def')}</th>
                    </tr>
                </thead>
                <tbody>
                    {defects.length === 0 ? (
                        <tr>
                            <td colSpan={6} className={styles.emptyState}>
                                {translate('No defects')}
                            </td>
                        </tr>
                    ) : (
                        defects.map((d, index) => (
                            <React.Fragment key={d.newId || d.id || d.defectId || index}>
                                <tr className={index % 2 === 1 ? styles.rowAlt : undefined}>
                                    <td>{d.defectCode?.code ?? d.defectId}</td>
                                    <td className={styles.description}>{d.defectCode?.codeDescription ?? '-'}</td>
                                    <td>{getDefectSeverity(d, DefectSeverityEnum.CRITICAL)}</td>
                                    <td>{getDefectSeverity(d, DefectSeverityEnum.MAJOR)}</td>
                                    <td>{getDefectSeverity(d, DefectSeverityEnum.MINOR)}</td>
                                    <td className={`${styles.defectImage} ${styles.defectColumn}`}>
                                        <DefectImage
                                            defect={d}
                                            inspectionId={inspectionId}
                                            zoomTitle={d.defectCode?.codeDescription ?? ''}
                                            className={styles.thumbnail}
                                        />
                                    </td>
                                </tr>
                                <tr className={index % 2 === 1 ? styles.rowAlt : undefined}>
                                    <td colSpan={6} className={styles.commentCell}>
                                        <span className={styles.commentLabel}>{translate('Comment')}:</span>
                                        <span className={styles.commentValue}>{d.comment || '-'}</span>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};
